/**
 * reportsHarvestInspections sagas
 */
import {
    call,
    delay,
    put,
    race,
    select,
    take,
    takeLatest
} from 'redux-saga/effects';
import get from 'lodash.get';
import fileDownload from 'js-file-download';
import moment from 'moment';

// Types
import type { Saga } from 'redux-saga';

// Constants
import {
    REPORTS_HARVEST_INSPECTIONS_LOAD_REQUESTED,
    REPORTS_HARVEST_INSPECTIONS_POLLING_REQUESTED,
    REPORTS_HARVEST_INSPECTIONS_POLLING_CANCELED,
    REPORTS_HARVEST_INSPECTIONS_EXPORT_REQUESTED
} from './constants';

// Actions
import setHash from '../hashes';
import { enqueueSnackbar } from '../notifications/actions';
import {
    succeededLoadReportsHarvestInspections,
    failedLoadReportsHarvestInspections,
    succeededPollingReportsHarvestInspections,
    failedPollingReportsHarvestInspections,
    succeededExportReportsHarvestInspections,
    failedExportReportsHarvestInspections,
    setReportsHarvestInspections
} from './actions';

// Selectors
import { selectCurrentOrganizationId } from '../currentOrganizationId/selectors';
import { selectCurrentSeasonId } from '../currentSeasonId/selectors';
import { selectCurrentEntityId } from '../currentEntityId/selectors';
import { makeSelectHash } from '../hashes/selectors';
import { selectIsOnline } from '../connection/selectors';

// Utils
import axiosRetry from '../../utils/axiosRetry';

// Environment
const REACT_APP_API_URL: string = String(process.env.REACT_APP_API_URL);

// Constants
const namespace = 'reportsHarvestInspections';

type Action = {
    +type: string,
    +payload: Object
};

type Config = ?{
    headers?: Object,
    params?: Object
};

export function* getReportsHarvestInspections(config: Config): Saga<void> {
    const organizationId = yield select(selectCurrentOrganizationId);
    const seasonId = yield select(selectCurrentSeasonId);
    const entityId = yield select(selectCurrentEntityId);

    const url = `${REACT_APP_API_URL}/v1/Reports/harvestInspections`;

    return yield call(axiosRetry, {
        method: 'GET',
        url,
        ...(config || {}),
        params: {
            ...(config && config.params ? config.params : {}),
            organizationId,
            filter: {
                require: {
                    $and: [
                        {
                            seasonId,
                            ...(entityId
                                ? { grandParentEntityId: entityId }
                                : {})
                            // TODO Filter out archived entities from reporting
                            // 'event.entity.archivedAt': { $exists: false }
                        }
                    ]
                },
                eager: 'event.jobs',
                fields: [
                    'event.startDate',
                    'event.id',
                    'event.jobs.jobType',
                    'event.jobs.jobState'
                ]
            }
        }
    });
}

export function* loadReportsHarvestInspections(action: Action): Saga<void> {
    try {
        const response = yield call(getReportsHarvestInspections);

        const hash = get(response, 'headers.x-hash');

        // Succeeded Load Reports Harvest Inspections
        yield put(succeededLoadReportsHarvestInspections(response));

        // Set Reports Harvest Inspections
        yield put(setReportsHarvestInspections(response.data.data));

        // Set Hash
        yield put(setHash(namespace, hash));
    } catch (error) {
        yield put(failedLoadReportsHarvestInspections(error));
    }
}

export function* watchReportsHarvestInspectionsLoadRequested(): Saga<void> {
    yield takeLatest(
        REPORTS_HARVEST_INSPECTIONS_LOAD_REQUESTED,
        loadReportsHarvestInspections
    );
}

export function* pollReportsHarvestInspections(action: Action): Saga<void> {
    while (true) {
        yield delay(60000);

        // Select Is Online
        const isOnline = yield select(selectIsOnline);

        // Select Hash
        const hash = yield select(makeSelectHash(namespace));
        let hashMatch = false;

        if (isOnline) {
            try {
                if (hash) {
                    const hashResponse = yield call(
                        getReportsHarvestInspections,
                        {
                            params: { hash: true }
                        }
                    );

                    hashMatch = hash === get(hashResponse, 'headers.x-hash');
                }
                if (!hashMatch) {
                    const response = yield call(getReportsHarvestInspections);

                    const nextHash = get(response, 'headers.x-hash');

                    // Succeeded Polling Reports Harvest Inspections
                    yield put(
                        succeededPollingReportsHarvestInspections(response)
                    );

                    // Set Reports Harvest Inspections
                    yield put(setReportsHarvestInspections(response.data.data));

                    // Set Hash
                    yield put(setHash(namespace, nextHash));
                }
            } catch (error) {
                // Failed Polling Reports Harvest Inspections
                yield put(failedPollingReportsHarvestInspections(error));
            }
        }
    }
}

export function* watchReportsHarvestInspectionsPollingRequested(): Saga<void> {
    while (true) {
        const action = yield take(
            REPORTS_HARVEST_INSPECTIONS_POLLING_REQUESTED
        );
        yield race([
            call(pollReportsHarvestInspections, action),
            take(REPORTS_HARVEST_INSPECTIONS_POLLING_CANCELED)
        ]);
    }
}

export function* exportReportsHarvestInspections(): Saga<void> {
    try {
        const response = yield call(getReportsHarvestInspections, {
            headers: {
                'Content-Type': 'text/csv; charset=utf-8',
                Accept: 'text/csv'
            }
        });

        const time = moment.utc().format();

        // File Download
        fileDownload(response.data, `reports_harvest_inspections_${time}.csv`);

        // Enqueue Snackbar
        yield put(
            enqueueSnackbar({
                message: 'Export successful',
                options: { variant: 'success' }
            })
        );

        // Succeeded Export Reports Harvest Inspections
        yield put(succeededExportReportsHarvestInspections(response));
    } catch (error) {
        const message = get(
            error,
            'response.data.message',
            'There was an error. Please try again later.'
        );

        // Enqueue Snackbar
        yield put(enqueueSnackbar({ message, options: { variant: 'error' } }));

        // Failed Export Reports Harvest Inspections
        yield put(failedExportReportsHarvestInspections(error));
    }
}

export function* watchReportsHarvestInspectionsExportRequested(): Saga<void> {
    yield takeLatest(
        REPORTS_HARVEST_INSPECTIONS_EXPORT_REQUESTED,
        exportReportsHarvestInspections
    );
}
